export const FRAME_MS = 50;
export const RMS_ACTIVE_THRESHOLD = 0.01;
export const MIN_AUDIBLE_MS = 1000;
const MAX_BLOB_BYTES = 20 * 1024 * 1024;
const DECODE_TIMEOUT_MS = 30000;

function withTimeout(promise, ms, message) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function decodeAudioBuffer(context, arrayBuffer) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };
    try {
      const maybePromise = context.decodeAudioData(
        arrayBuffer,
        finish(resolve),
        finish(reject)
      );
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(finish(resolve), finish(reject));
      }
    } catch (error) {
      finish(reject)(error);
    }
  });
}

function analyzeBuffer(audioBuffer) {
  const { sampleRate, numberOfChannels, length } = audioBuffer;
  if (!length || !numberOfChannels) return true;

  const channels = Array.from({ length: numberOfChannels }, (_, index) =>
    audioBuffer.getChannelData(index)
  );
  const frameSize = Math.max(1, Math.round(sampleRate * (FRAME_MS / 1000)));
  let activeMs = 0;

  for (let start = 0; start < length; start += frameSize) {
    const end = Math.min(start + frameSize, length);
    const sampleCount = end - start;
    if (sampleCount <= 0) continue;

    let maxChannelRms = 0;
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      const data = channels[channel];
      let sumSquares = 0;
      for (let i = start; i < end; i += 1) {
        const sample = data[i];
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / sampleCount);
      if (rms > maxChannelRms) maxChannelRms = rms;
    }

    if (maxChannelRms >= RMS_ACTIVE_THRESHOLD) {
      activeMs += FRAME_MS;
      if (activeMs >= MIN_AUDIBLE_MS) return false;
    }
  }

  return true;
}

export async function isAudioSilent(blob) {
  if (!blob) return false;
  if (blob.size === 0) return true;
  if (blob.size > MAX_BLOB_BYTES) return false;

  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return false;

  let context;
  try {
    context = new AudioCtx();
    if (context.state === 'suspended') {
      await context.resume();
    }

    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await withTimeout(
      decodeAudioBuffer(context, arrayBuffer),
      DECODE_TIMEOUT_MS,
      'Audio decode timed out'
    );

    return analyzeBuffer(audioBuffer);
  } catch (error) {
    console.warn('Silence detection skipped:', error?.message || error);
    return false;
  } finally {
    try {
      await context?.close();
    } catch {
      // ignore close errors
    }
  }
}
